import React, { useState, useEffect } from "react";
import { createModalConfig, ModalConfig } from "../../../Models/ModalConfig";
import { Shipment } from "../../../Models/Shipment";
import { ShipmentsService } from "../../../services/shipment/shipments";
import { callErrorToast } from "../../../utilities/utilities";
import { useParams } from "react-router-dom";
import {
  ShipmentDetailsContextValue,
  ShipmentDetailsProviderProps,
} from "./types";
import { ShipmentDetailsContext } from "./contexts";
import { useQuery } from "../../../utilities";
import { useAppContext } from "../../../AppProvider";

// Service instance for fetching shipment data
const shipmentsService = new ShipmentsService();

/* ===========================================================================
 * PROVIDER COMPONENT
 * =========================================================================== */

/**
 * @component ShipmentDetailsProvider
 *
 * Fetches detailed shipment data for a given shipment ID, handles
 * loader and error states, and exposes modal controls and context
 * values for child components.
 */
export const ShipmentDetailsProvider: React.FC<
  ShipmentDetailsProviderProps
> = ({ children }) => {
  const { dictionary } = useAppContext(); // Global dictionary for messages/text
  const [data, setData] = useState<Partial<Shipment>>({}); // Shipment data
  const [loader, setLoader] = useState<boolean>(true); // Loading state
  const [modalConfig, setModalConfig] =
    useState<ModalConfig>(createModalConfig()); // Modal state

  // acr = Ask Collection Reservation
  // const [acr, setAcr] = useState<boolean>(false);

  const { id } = useParams(); // Get shipment ID from route parameters
  const query = useQuery(); // Custom hook to parse query params

  /**
   * Fetches shipment details by ID from backend.
   *
   * If certain conditions are met (carrier allows collection creation,
   * collection not yet assigned, acr is true, and shipment status is
   * BOOKED or CONFIRMED), automatically opens a modal to prompt the user.
   *
   * @param {string} id - Shipment ID to fetch
   */
  const getShipmentDetails = async (id: string) => {
    setLoader(true);
    try {
      const res = await shipmentsService.get(id);
      setData(res.data);

      // Automatically show collection booking modal if conditions match
      // if (
      //   res.data.contractService?.carrierService?.carrier
      //     .collectionCreationEnabled &&
      //   !res.data.collectionAssigned &&
      //   acr &&
      //   (res.data.status.code === "BOOKED" ||
      //     res.data.status.code === "CONFIRMED")
      // ) {
      //   setModalConfig({
      //     isOpen: true,
      //     size: "md",
      //     action: "createCollection",
      //     data: [id],
      //     title: dictionary.collections.do_you_want_to_book_a_collection,
      //   });
      // }

      return res;
    } catch (err) {
      callErrorToast(err);
      throw err;
    } finally {
      setLoader(false);
    }
  };

  /* ---------------------------------------------------------------------------
   * EFFECTS
   * ------------------------------------------------------------------------- */

  // Fetch shipment details whenever the route param ID changes
  useEffect(() => {
    if (id) {
      getShipmentDetails(id);
      return;
    }
    setLoader(false);
  }, [id]);

  // Read `acr` (ask collection reservation) flag from query params
  // useEffect(() => {
  //   const acr = query.get("acr");
  //   setAcr(acr === "true");
  // }, []);

  /* =========================================================================
   * CONTEXT VALUE
   * ======================================================================= */
  const contextValue: ShipmentDetailsContextValue = {
    modalConfig,
    setModalConfig,
    data,
    loader,
    getShipmentDetails,
    // acr,
    // setAcr,
  };

  return (
    <ShipmentDetailsContext.Provider value={contextValue}>
      {children}
    </ShipmentDetailsContext.Provider>
  );
};
